import {SettingItemProps} from "@/components/settings/SettingMultiple";
import {Edge, Node, NodeProps as ReactFlowNodeProps} from "@xyflow/react";
import {Variable} from "@/contexts/VariablesContext";
import {BookingAction} from "@/components/settings/Actions/SelectActionsTable";
import {TransferCallAction} from "@/components/settings/Actions/SelectActionsTable";
import {CustomAction} from "@/api/services/customAction";
export interface NodeProps extends ReactFlowNodeProps {
  data: FlowNodeData;
}

export enum SettingParamTypes {
  SELECT = "select",
  NODE_SELECT = "node_select",
  INPUT = "input",
  TEXTAREA = "textarea",
  PROMPT = "prompt",
  ACTIONS = "actions",
  MODAL = "modal",
  OBJECTION = "objection",
  FAQ = "faq",
  CAPTURE = "capture",
  ENDPOINT = "endpoint",
  MULTIPLE = "multiple",
  SWITCH = "switch",
  TABS = "tabs",
  AUTHENTICATE = "authenticate",
  JSON = "json",
  VARIABLE_SELECT = "variable_select",
  VALUE = "value",
  DETERMINISTIC_CONDITION = "deterministic_condition",
}

export enum FlowNodeTypes {
  PLACEHOLDER = "placeholder",
  ROOT = "root",
  GLOBAL = "global",
  START = "start",
  COLLECT_VARIABLES = "collect_variables",
  SPEAK = "speak",
  BRANCH = "branch",
  JUMP = "jump",
  END = "end",
  ACTION = "action",
  STEP = "step",
  TRANSFER = "transfer",
  ENTRY = "entry",
  BOOKING = "booking",
  INSTRUCTION = "instruction",
}

export enum ActionTypes {
  CUSTOM_ACTION = "CUSTOM_ACTION",
  LIVE_TRANSFER = "LIVE_TRANSFER",
  REAL_TIME_BOOKING = "REAL_TIME_BOOKING",
}

export enum SavingState {
  SAVING = "SAVING",
  SAVED = "SAVED",
  ERROR = "ERROR",
  LOADING = "LOADING",
  ERROR_ACTIONS = "ERROR_ACTIONS",
  ERROR_LOADING = "ERROR_LOADING",
}

export enum VariableTypes {
  VARIABLES = "variables",
  RESULTS = "results",
  ACTIONS = "actions",
}

export enum FlowMode {
  LIVE = "live",
  TEST = "test",
}

export interface AuthValue {
  authType?: string;
  data?: {
    location?: string;
    key?: string;
    type?: string;
    value?: string;
  };
}

export interface ProcessedNode {
  id: string;
  type: FlowNodeTypes;
  data: {
    settings: FlowNodeSetting;
    type: FlowNodeTypes;
  };
}

export interface FlowItem {
  version: number;
  nodes: Node[];
  edges: Edge[];
}

export interface FlowData {
  name?: string;
  live: {
    version: number;
    nodes: Node<FlowNodeData>[];
    edges: Edge[];
  };
  test: {
    version: number;
    nodes: Node<FlowNodeData>[];
    edges: Edge[];
  };
}

export interface FlowDataItem {
  version: number;
  nodes: Node[];
  edges: Edge[];
}

export interface FlowDataProcessed {
  live: FlowItem;
  test: FlowItem;
}
export enum AssistantType {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export interface PromptAction {
  name: string;
  http_method: string;
  action_results: string[];
  action_id?: string;
}

export interface QueryParameters {
  key: string;
  value: string;
}
export interface ActionNodeData {
  action_id: string;
  slug: string;
  type: string;
  endpoint?: EndpointValue;
  headers?: HeadersValue;
  authentication?: AuthenticationValue;
  body_and_variables?: BodyAndVariablesValue;
  start_message?: string;
  delay_message?: string;
  failure_message?: string;
  delay_message_time?: string;
  failure_timeout?: number;
  run_action_before_call_start?: boolean;
  speak_naturally?: boolean;
  name?: string;
  description?: string;
  variables?: string[];
  attachment_type?: string;
  query_parameters?: QueryParameters[];
}
export interface FlowCustomActionModel {
  action?: any; // Hidden value field
  action_id?: string;
  endpoint: EndpointValue;
  headers?: HeadersValue;
  authentication?: {
    custom?: any;
    hubspot?: any;
    ghl?: any;
    salesforce?: any;
  };
  body_and_variables?: BodyAndVariablesValue;
  speak_naturally?: boolean;
  start_message?: string;
  delay_message?: string;
  failure_message?: string;
  action_name?: string;
  description?: string;
}

export interface EndpointValue {
  url: string;
  http_method: string;
}

export interface AuthenticationValue {
  [key: string]: {
    selected: boolean;
    [authKey: string]:
      | {
          authType: string;
          data: {
            location: string;
            key: string;
            type: string;
            value: string;
          };
        }
      | boolean;
  };
}

export interface BodyAndVariablesValue {
  json?: {
    selected?: boolean;
    body?: string;
  };
  variables?: {
    selected?: boolean;
    variables?: Array<Array<{key: string; value: string}>>;
  };
  query_parameters?: {
    selected?: boolean;
    query_parameters?: Array<Array<{key: string; value: string}>>;
  };
}

export type HeadersValue = Array<Array<{key: string; value: string}>>;

export type CustomActionHeaders = {[key: string]: string};

export type PromptVariable = PromptAction | string;

export interface FlowNodeStyles {
  name: string;
  icon: React.ElementType;
  iconInverted?: React.ElementType;
  blueprint: any;
  styles: {
    iconColor?: string;
    iconBackgroundColor?: string;
    iconBorderColor?: string;
    minimapNodeColor?: string;
    miniMapNodeSelectedColor?: string;
    contextColor?: string;
    backgroundColor?: string;
    backgroundColorActive?: string;
    inverted?: boolean;
    borderColor?: string;
    borderColorHover?: string;
    borderColorActive?: string;
  };
}

export interface SettingsProps {
  selectedNode: Node<FlowNodeData>;
  takeSnapshot: () => void;
  assistantType?: AssistantType;
  promptVariables?: any;
  className?: string;
  saveOnChange?: boolean;
  onSettingChange?: (
    param: SettingParam,
    value: SettingItemProps[],
    onError?: (error: string) => void,
  ) => void;
}

// TODO: Divide into multiple types for each setting type
export type SettingParam = {
  hidden?: boolean;
  label?: string;
  key?: string;
  value?:
    | string
    | number
    | boolean
    | undefined
    | null
    | Record<any, string | number | boolean | undefined>[]
    | Record<any, string | number | boolean | undefined>
    | EndpointValue
    | Array<SettingItemProps[]>
    | AuthValue
    | CustomAction
    | BookingAction
    | TransferCallAction
    | string[];
  type: SettingParamTypes;
  placeholder?: string;
  error?: string;
  modalSettings?: SettingParam[];
  settings?: SettingParam[];
  settingName?: string;
  modalDescription?: string;
  unitName?: string;
  required?: boolean;
  requiredLabel?: string;
  selectOptions?: {name: string; value: string}[];
  maxLength?: number;
  className?: string;
  tabs?: {
    title: string;
    settings: SettingParam[];
    value: SettingParam["value"];
    selected?: boolean;
    key?: string;
  }[];
  variables?: Variable[];
  tooltipMessage?: string;
  validationSchema?: any;
};

export type Condition = {
  variable: Variable | null;
  operator: string;
  value: string;
};

export type ObjectionItem = {
  title: string;
  description: string;
};

export type FlowNodeSetting = {
  title: string;
  description?: string;
  icon?: any;
  params: SettingParam[];
  key?: string;
  scripted_greeting?: string;
  expandedSetting?: boolean;
  variables?: Variable[];
  instructions?: string;
  global_instructions?: string;
  condition_type?: {
    deterministic: {
      rules: Condition[];
      is_and?: boolean;
    };
    prompt: {
      description: string;
    };
  };
  special_cases?: ObjectionItem[];
  jump_to_step?: string;
  action?: CustomAction | BookingAction | TransferCallAction;
  endpoint?: EndpointValue;
  headers?: CustomActionHeaders;
  action_id?: string;
  placeholder?: string;
  verbatim?: Record<string, {selected?: boolean}>;
};

export type FlowNodeData = {
  type: FlowNodeTypes;
  description?: string;
  instructions?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  settingsTitle?: string;
  nodeTitle?: string;
  settings?: FlowNodeSetting[];
  children?: string[];
  parentId?: string;
  label?: string;
  isEmpty?: boolean;
  name?: string;
  validationErrors?: Record<string, string>;
  actionError?: {
    status: "not_found";
    message: string;
  };
};

export type CustomSettingData = {
  type: string;
  description?: string;
  settings?: FlowNodeSetting[];
  settingsTitle?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export interface FlowIssue {
  type: "error" | "warning";
  message: string;
  nodeId: string;
  nodeType: FlowNodeTypes;
  key: string;
  nodeTitle: string;
  subflowId?: string;
  subflowName?: string;
}

export interface ActionVariable {
  key: string;
  value: string;
  id: string;
}
